import {
  applyCitationStatusChanges,
  applyHelpRequestUpsert,
  type AttachedStatementReference,
  resolveNeedsCitation,
  type StatementCitationStatus,
  type StatementReferenceHelpRequest,
} from './extractedStatements';
import { isValidReferenceUrl } from './referenceUrl';

/**
 * Author-side write surface for per-statement references on the
 * ExtractedStatementsBubble, entered through the citation wizard's
 * provide phase.
 *
 * # What gets persisted (v1)
 *
 * Each entry in `attachedStatementReferences` is just `{ statementIndex,
 * url }`. We do NOT resolve titles, journals, or authors at publish time
 * — that's deferred to a follow-up that wires the format-references-
 * batch resolver back into this surface. Renderers fall back to the
 * URL hostname (or the URL itself) for display until then.
 *
 * # Read-modify-write contract
 *
 * The statement-references `PUT` replaces each array the body carries,
 * whole; there is no partial-array operation. The publish function takes
 * the caller's current arrays as explicit parameters (the latest arrays the
 * bubble has rendered), appends or upserts, and writes the result.
 *
 * # Concurrent-write tradeoff
 *
 * If two browser tabs with the same expert author both publish at the
 * same instant, both reads see the same starting array, both compute
 * their next array independently, and both write. The last write wins;
 * one update is silently lost. We accept this in v1: the realistic
 * shape of usage is one author with one foreground tab. If telemetry
 * shows real loss, the resolution is moving the append/upsert to a
 * server-side endpoint; this client API can stay as-is.
 */

/**
 * The statement-owned state the author writes. Each array present here
 * replaces the stored one whole.
 */
export type StatementReferencesPatch = {
  attachedStatementReferences?: AttachedStatementReference[];
  statementReferenceHelpRequests?: StatementReferenceHelpRequest[];
  statementCitationStatuses?: StatementCitationStatus[];
};

/** Discriminator for `StatementReferenceError`. */
export type StatementReferenceErrorKind = 'persist_failed';

/**
 * Error thrown by `publishProvidedStatementReferences`. Carries a
 * discriminator so callers (analytics, UI) can map to user-facing copy
 * without `instanceof` chains on the original cause.
 */
export class StatementReferenceError extends Error {
  constructor(
    public readonly kind: StatementReferenceErrorKind,
    message: string,
    public readonly cause?: unknown
  ) {
    super(message);

    this.name = 'StatementReferenceError';
  }
}

/** One provide-phase row: the single free-text value for a statement. */
export interface ProvideStatementReferenceEntry {
  statementIndex: number;
  /** The expert's input. Routed by content at publish: a URL attaches
   * verbatim as a reference; any other non-empty text becomes a help
   * request. Empty is skipped. */
  value: string;
}

/** Input contract for `publishProvidedStatementReferences`. */
export interface PublishProvidedStatementReferencesInput {
  conversationId: string;
  messageSid: string;
  /** One entry per provide row. Empty rows (no URL, no description) are
   * skipped. */
  entries: readonly ProvideStatementReferenceEntry[];
  /** `attachedStatementReferences` array as currently rendered — the
   * read-modify-write base for the URL append. */
  currentReferences: readonly AttachedStatementReference[];
  /** `statementReferenceHelpRequests` array as currently rendered — the
   * upsert base for describe-it rows. */
  currentHelpRequests?: readonly StatementReferenceHelpRequest[];
  /** `statementCitationStatuses` array as currently rendered — the base
   * for the `needsCitation: true` mark folded into the same write. */
  currentStatementCitationStatuses?: readonly StatementCitationStatus[];
}

export interface PublishProvidedStatementReferencesResult {
  /** New URL entries appended this write (post-dedupe). */
  appendedReferences: AttachedStatementReference[];
  /** Statement indices whose describe-it draft was upserted as a help
   * request this write. */
  helpRequestedStatementIndices: number[];
  /** Statement indices flipped to `needsCitation: true` this write. */
  autoMarkedStatementIndices: number[];
  /** `true` when the write was a no-op (nothing to persist). */
  noop: boolean;
}

/**
 * Plan a citation-wizard **provide** phase: the one write that
 * writes verbatim URL references and describe-it help requests for the
 * selected statements. No citation request, no
 * search, no `formatReferencesBatch` enrichment — this is the no-search
 * sibling of the search flow's `approveCitationRequest`.
 *
 * Each row carries one free-text `value`, routed by content:
 * - Detected as a URL (`isValidReferenceUrl`) → appended verbatim as
 *   `{ statementIndex, url }` (deduped by `statementIndex::url` against
 *   `currentReferences`).
 * - Any other non-empty text → upserted as a
 *   `StatementReferenceHelpRequest` via `applyHelpRequestUpsert`.
 * - Empty → skipped.
 *
 * Every statement that received a URL or a help request is also flipped
 * to `needsCitation: true` in the same write, so the bubble reflects
 * the new citation intent.
 *
 * A write that resolves to no changes (all rows empty, or every URL a
 * duplicate and no help requests / status flips) is a no-op — no
 * patch to send, `noop: true`.
 *
 * Last write wins, the tradeoff documented in the module JSDoc.
 */
export function planProvidedStatementReferences(input: PublishProvidedStatementReferencesInput): {
  patch?: StatementReferencesPatch;
  result: PublishProvidedStatementReferencesResult;
} {
  const urlEntries: AttachedStatementReference[] = [];

  const helpRequestEntries: { statementIndex: number; description: string }[] = [];

  for (const entry of input.entries) {
    const trimmed = entry.value.trim();

    if (trimmed.length === 0) {
      continue;
    }

    if (isValidReferenceUrl(trimmed)) {
      urlEntries.push({ statementIndex: entry.statementIndex, url: trimmed });
    } else {
      helpRequestEntries.push({ statementIndex: entry.statementIndex, description: trimmed });
    }
  }

  // Dedupe URL entries against what's already attached so a retry after
  // a transient error doesn't double-write.
  const existingKeys = new Set(
    input.currentReferences.map((reference) => `${reference.statementIndex}::${reference.url}`)
  );

  const appendedReferences = urlEntries.filter(
    (entry) => !existingKeys.has(`${entry.statementIndex}::${entry.url}`)
  );

  // Help requests: upsert each non-empty describe-it row.
  let nextHelpRequests = input.currentHelpRequests
    ? [...input.currentHelpRequests]
    : ([] as StatementReferenceHelpRequest[]);

  const helpRequestedStatementIndices: number[] = [];

  for (const entry of helpRequestEntries) {
    nextHelpRequests = applyHelpRequestUpsert(nextHelpRequests, {
      statementIndex: entry.statementIndex,
      description: entry.description,
    });

    helpRequestedStatementIndices.push(entry.statementIndex);
  }

  // Auto-mark needsCitation: true for every statement that received a
  // URL or a help request and isn't already marked.
  const statusChanges = new Map<number, boolean>();

  for (const statementIndex of [
    ...appendedReferences.map((entry) => entry.statementIndex),
    ...helpRequestedStatementIndices,
  ]) {
    if (!resolveNeedsCitation(input.currentStatementCitationStatuses, statementIndex)) {
      statusChanges.set(statementIndex, true);
    }
  }

  const hasReferenceWrite = appendedReferences.length > 0;

  const hasHelpRequestWrite = helpRequestEntries.length > 0;

  const hasStatusWrite = statusChanges.size > 0;

  if (!hasReferenceWrite && !hasHelpRequestWrite && !hasStatusWrite) {
    return {
      result: {
        appendedReferences: [],
        helpRequestedStatementIndices: [],
        autoMarkedStatementIndices: [],
        noop: true,
      },
    };
  }

  const patchBody: StatementReferencesPatch = {};

  if (hasReferenceWrite) {
    patchBody.attachedStatementReferences = [...input.currentReferences, ...appendedReferences];
  }

  if (hasHelpRequestWrite) {
    patchBody.statementReferenceHelpRequests = nextHelpRequests;
  }

  const nextStatuses = hasStatusWrite
    ? applyCitationStatusChanges(input.currentStatementCitationStatuses, statusChanges)
    : undefined;

  if (nextStatuses) {
    patchBody.statementCitationStatuses = nextStatuses;
  }

  return {
    patch: patchBody,
    result: {
      appendedReferences,
      helpRequestedStatementIndices,
      autoMarkedStatementIndices: [...statusChanges.keys()],
      noop: false,
    },
  };
}
